import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, RefreshCw } from "lucide-react";
import { useLang } from "@/i18n";
import { EASE } from "@/lib/site";
import { Reveal, SectionHead } from "./Primitives";
import { Festivals } from "./Festivals";

const SIGNS = [
  ["mesha", "♈"], ["vrishabha", "♉"], ["mithuna", "♊"], ["karka", "♋"], ["simha", "♌"], ["kanya", "♍"],
  ["tula", "♎"], ["vrishchika", "♏"], ["dhanu", "♐"], ["makara", "♑"], ["kumbha", "♒"], ["meena", "♓"],
];
const API = process.env.REACT_APP_BACKEND_URL;

const useRashifal = (lang) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const load = useCallback(async () => {
    setState({ status: "loading", data: null });
    if (!API) { setState({ status: "error", data: null }); return; }
    try {
      const res = await axios.get(`${API}/api/rashifal`, { params: { lang }, timeout: 180000 });
      setState(res.data?.items ? { status: "ready", data: res.data } : { status: "error", data: null });
    } catch {
      setState({ status: "error", data: null });
    }
  }, [lang]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
};

const SignGrid = ({ t, active, setActive }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2.5">
    {SIGNS.map(([key, glyph]) => (
      <button key={key} data-testid={`rashi-btn-${key}`} onClick={() => setActive(key)}
        className={`rashi-tile ${active === key ? "is-active" : ""}`}>
        <span className="text-3xl leading-none">{glyph}{"\uFE0E"}</span>
        <span className="mt-2 text-sm font-medium">{t.rashifal.signs[key]}</span>
        <span className="text-[11px] uppercase tracking-[0.18em] opacity-70">{t.rashifal.lords[key]}</span>
      </button>
    ))}
  </div>
);

const Field = ({ label, text }) => (
  <div className="border-t border-[color:var(--line)] pt-5">
    <p className="eyebrow !text-[11px] mb-2">{label}</p>
    <p className="leading-relaxed text-[color:var(--ink)]/90">{text}</p>
  </div>
);

const Detail = ({ t, item, sign }) => (
  <motion.div key={sign} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} data-testid="rashifal-detail">
    <div className="flex items-end gap-5">
      <span className="display text-7xl sm:text-8xl text-[color:var(--saffron)] leading-none">{SIGNS.find((s) => s[0] === sign)[1]}{"\uFE0E"}</span>
      <div>
        <h3 data-testid="rashifal-detail-name" className="display text-4xl sm:text-5xl">{t.rashifal.signs[sign]}</h3>
        <p className="text-sm text-[color:var(--muted)]">{t.rashifal.lords[sign]}</p>
      </div>
    </div>
    <p data-testid="rashifal-detail-overview" className="mt-8 text-lg sm:text-xl leading-relaxed display italic text-[color:var(--ink)]">{item?.overview}</p>
    <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6">
      <Field label={t.rashifal.career} text={item?.career} />
      <Field label={t.rashifal.love} text={item?.love} />
      <Field label={t.rashifal.health} text={item?.health} />
      <Field label={t.rashifal.remedy} text={item?.remedy} />
    </div>
    <div className="mt-8 flex flex-wrap gap-3">
      <span data-testid="rashifal-lucky-color" className="chip">{t.rashifal.lucky_color}: <b className="ml-1">{item?.lucky_color}</b></span>
      <span data-testid="rashifal-lucky-number" className="chip">{t.rashifal.lucky_number}: <b className="ml-1">{item?.lucky_number}</b></span>
    </div>
  </motion.div>
);

const Status = ({ t, status, reload }) => (
  <div data-testid="rashifal-status" className="min-h-[360px] flex flex-col items-start justify-center gap-4 text-[color:var(--muted)]">
    {status === "loading" ? (
      <p className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--saffron)]" /> {t.rashifal.loading}</p>
    ) : (
      <>
        <p>{t.rashifal.error}</p>
        <button data-testid="rashifal-retry-btn" onClick={reload} className="pill-ghost px-5 py-2.5 text-sm"><RefreshCw className="w-4 h-4 mr-2" />{t.rashifal.retry}</button>
      </>
    )}
  </div>
);

export const Rashifal = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState("mesha");
  const { status, data, reload } = useRashifal(lang);
  const month = data?.month ? new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(new Date(`${data.month}-01T12:00:00`)) : "";
  return (
    <section id="rashifal" data-testid="rashifal-section" className="relative py-24 lg:py-32 bg-[color:var(--surface)] overflow-hidden">
      <div className="mandala absolute -left-60 -top-40 w-[700px] h-[700px] opacity-20 pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={`${t.rashifal.eyebrow}${month ? ` · ${month}` : ""}`} title={t.rashifal.title} desc={t.rashifal.desc} testid="rashifal" />
        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5"><SignGrid t={t} active={active} setActive={setActive} /></Reveal>
          <div className="lg:col-span-7 card p-6 sm:p-10">
            {status === "ready" ? <Detail t={t} item={data.items?.[active]} sign={active} /> : <Status t={t} status={status} reload={reload} />}
            <p className="mt-10 text-xs text-[color:var(--muted)]">{t.rashifal.note}</p>
          </div>
        </div>
        <Festivals />
      </div>
    </section>
  );
};
